import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usersApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Users, Search, Upload, BarChart3, Eye, DollarSign, KeyRound, Cake, Sparkles, UserX, UserCheck, LayoutList } from "lucide-react";

// Employee data from Excel
const EMPLOYEE_DATA = [
  { first_name: "Aaron", last_name: "Bartholomew", email: "abartholomew@steelemartin.com", position: "Staff Accountant" },
  { first_name: "Alissa", last_name: "Farley", email: "afarley@steelemartin.com", position: "Staff Accountant" },
  { first_name: "Allison", last_name: "Rogers", email: "arogers@steelemartin.com", position: "Staff Accountant" },
  { first_name: "Angela", last_name: "Smith", email: "angela@steelemartin.com", position: "Staff Accountant" },
  { first_name: "Ashley", last_name: "Williams", email: "awilliams@steelemartin.com", position: "Staff Accountant" },
  { first_name: "Brandon", last_name: "Clenney", email: "brandon@steelemartin.com", position: "Partner" },
  { first_name: "Caitlin", last_name: "Clenney", email: "cclenney@steelemartin.com", position: "Clerical" },
  { first_name: "Carter", last_name: "Schrum", email: "cschrum@steelemartin.com", position: "Staff Accountant" },
  { first_name: "Christine", last_name: "Aden", email: "caden@steelemartin.com", position: "Staff Accountant" },
  { first_name: "Cullyn", last_name: "Moore", email: "cmoore@steelemartin.com", position: "Staff Accountant" },
  { first_name: "Dottie", last_name: "Kisner", email: "dkisner@steelemartin.com", position: "Front Office" },
  { first_name: "Erin", last_name: "Crooks", email: "ecrooks@steelemartin.com", position: "Staff Accountant" },
  { first_name: "Haley", last_name: "Montgomery", email: "hmontgomery@steelemartin.com", position: "Staff Accountant" },
  { first_name: "Jennifer", last_name: "Rhea", email: "jrhea@steelemartin.com", position: "Front Office" },
  { first_name: "John", last_name: "Wilmoth", email: "jwilmoth@steelemartin.com", position: "Staff Accountant" },
  { first_name: "Kathy", last_name: "Henson", email: "khenson@steelemartin.com", position: "Office Admin" },
  { first_name: "Kathy", last_name: "Keymon", email: "kkeymon@steelemartin.com", position: "Payroll" },
  { first_name: "Kelli", last_name: "Gough", email: "kgough@steelemartin.com", position: "Payroll" },
  { first_name: "Kelly", last_name: "Latimer", email: "klatimer@steelemartin.com", position: "Front Office" },
  { first_name: "Kevin", last_name: "McKenzie", email: "kmckenzie@steelemartin.com", position: "Partner" },
  { first_name: "Kristi", last_name: "Mendez", email: "kmendez@steelemartin.com", position: "Senior Accountant" },
  { first_name: "Nicholas", last_name: "Williamson", email: "nwilliamson@steelemartin.com", position: "Staff Accountant" },
  { first_name: "Noah", last_name: "Carbonell", email: "ncarbonell@steelemartin.com", position: "Staff Accountant" },
  { first_name: "Page", last_name: "Cole", email: "apcole@steelemartin.com", position: "Senior Accountant" },
  { first_name: "Stacey", last_name: "Byrd", email: "sbyrd@steelemartin.com", position: "Staff Accountant" },
  { first_name: "Tom", last_name: "Jones", email: "tjones@steelemartin.com", position: "Partner" },
  { first_name: "Tonia", last_name: "McKenzie", email: "tonia@steelemartin.com", position: "Clerical" },
  { first_name: "William", last_name: "Welch", email: "wwelch@steelemartin.com", position: "Partner" },
];

// Format an ISO timestamp as relative time (e.g. "3 min ago", "2 days ago").
// Falls back to a short date when older than 30 days. Returns "—" if missing.
const formatRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const diff = (Date.now() - d.getTime()) / 1000; // seconds
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) === 1 ? "" : "s"} ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / (86400 * 7))} wk ago`;
  return d.toLocaleDateString();
};

const EmployeeManagement = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [impersonating, setImpersonating] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(null);
  const { impersonate, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "employee",
    position: "",
    tracks_stats: true,
    birthday: "",
    hire_date: "",
  });
  const [seedingCelebrations, setSeedingCelebrations] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // full user object when editing
  const [editForm, setEditForm] = useState({ birthday: "", hire_date: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await usersApi.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await usersApi.create(formData);
      toast.success("Employee created successfully");
      setDialogOpen(false);
      setFormData({ name: "", email: "", password: "", role: "employee", position: "", tracks_stats: true, birthday: "", hire_date: "" });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create employee");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSeedCelebrations = async () => {
    setSeedingCelebrations(true);
    try {
      const res = await usersApi.seedCelebrations();
      const d = res.data;
      toast.success(d.message || "Celebrations seeded");
      if (d.unmatched?.length) {
        toast.message(`${d.unmatched.length} entries could not be matched`, {
          description: d.unmatched.map((u) => `${u.type}: ${u.name}`).join(", "),
        });
      }
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to seed celebrations");
    } finally {
      setSeedingCelebrations(false);
    }
  };

  const openEditCelebrations = (u) => {
    setEditingUser(u);
    setEditForm({ birthday: u.birthday || "", hire_date: u.hire_date || "" });
  };

  const handleSaveCelebrations = async () => {
    if (!editingUser) return;
    setSavingEdit(true);
    try {
      await usersApi.update(editingUser.id, {
        birthday: editForm.birthday,
        hire_date: editForm.hire_date,
      });
      toast.success(`${editingUser.name}'s dates updated`);
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleImportEmployees = async () => {
    setImporting(true);
    try {
      const result = await usersApi.importBulk(EMPLOYEE_DATA);
      toast.success(result.data.message);
      setImportDialogOpen(false);
      loadUsers();
    } catch (error) {
      toast.error("Failed to import employees");
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (userId, userName) => {
    try {
      await usersApi.delete(userId);
      toast.success(`${userName} has been removed`);
      loadUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleImpersonate = async (userId, userName) => {
    setImpersonating(userId);
    try {
      const impersonatedUser = await impersonate(userId);
      toast.success(`Now viewing as ${userName}`);
      // Navigate to appropriate dashboard based on role
      if (impersonatedUser.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error("Failed to impersonate user");
    } finally {
      setImpersonating(null);
    }
  };

  const handleToggleBillings = async (userId, currentValue, userName) => {
    try {
      await usersApi.update(userId, { tracks_billings: !currentValue });
      toast.success(`${userName} ${!currentValue ? 'can now' : 'no longer'} track billings`);
      loadUsers();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleToggleSupervisor = async (userId, currentValue, userName) => {
    try {
      await usersApi.update(userId, { is_supervisor: !currentValue });
      toast.success(`${userName} ${!currentValue ? 'is now a supervisor' : 'is no longer a supervisor'}`);
      loadUsers();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleToggleActive = async (user) => {
    const deactivating = user.is_active !== false;
    setDeactivateTarget(null);
    try {
      await usersApi.update(user.id, { is_active: !deactivating });
      toast.success(
        deactivating
          ? `${user.name} deactivated — they can no longer log in.`
          : `${user.name} reactivated.`
      );
      loadUsers();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleToggleShelf = async (user) => {
    const on = user.shelf_participant !== false;
    try {
      await usersApi.update(user.id, { shelf_participant: !on });
      toast.success(
        on ? `${user.name} removed from the Digital Shelf.` : `${user.name} added back to the Digital Shelf.`
      );
      loadUsers();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleToggleStats = async (userId, currentValue, userName) => {
    try {
      await usersApi.update(userId, { tracks_stats: !currentValue });
      toast.success(`${userName} ${!currentValue ? 'now tracks' : 'no longer tracks'} stats`);
      loadUsers();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleResetPassword = async (userId, userName) => {
    setResettingPassword(userId);
    try {
      const response = await usersApi.regeneratePassword(userId);
      toast.success(
        <div>
          <p><strong>{userName}'s password reset!</strong></p>
          <p className="text-sm mt-1">New password: <code className="bg-muted px-1 rounded">{response.data.password}</code></p>
          <p className="text-xs text-muted-foreground mt-1">They'll need to change it on next login.</p>
        </div>,
        { duration: 10000 }
      );
      loadUsers();
    } catch (error) {
      toast.error("Failed to reset password");
    } finally {
      setResettingPassword(null);
    }
  };

  const inactiveCount = users.filter((u) => u.is_active === false).length;

  const filteredUsers = users
    .filter((user) => (showInactive ? true : user.is_active !== false))
    .filter(
      (user) =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employee-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Employee Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage your firm's employees and their access
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button
            variant="outline"
            className="gap-2"
            onClick={handleSeedCelebrations}
            disabled={seedingCelebrations}
            data-testid="seed-celebrations-btn"
          >
            {seedingCelebrations ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles size={18} />
            )}
            Seed Birthdays & Anniversaries
          </Button>
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="import-employees-btn">
                <Upload size={18} />
                Import from Excel
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg" data-testid="import-dialog">
              <DialogHeader>
                <DialogTitle>Import Employees from Excel</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  This will import {EMPLOYEE_DATA.length} employees from your uploaded Excel file.
                  Existing users will be skipped.
                </p>
                <div className="bg-muted rounded-lg p-4 max-h-64 overflow-y-auto">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Preview:</p>
                  <div className="space-y-1">
                    {EMPLOYEE_DATA.slice(0, 10).map((emp, i) => (
                      <div key={i} className="text-sm flex justify-between">
                        <span>{emp.first_name} {emp.last_name}</span>
                        <span className="text-muted-foreground">{emp.position}</span>
                      </div>
                    ))}
                    {EMPLOYEE_DATA.length > 10 && (
                      <p className="text-xs text-muted-foreground mt-2">...and {EMPLOYEE_DATA.length - 10} more</p>
                    )}
                  </div>
                </div>
                <div className="bg-blue-500/10 rounded-lg p-3 text-sm text-blue-700">
                  <p className="font-medium">Role assignment:</p>
                  <ul className="text-xs mt-1 space-y-0.5">
                    <li>• Partners & Office Admin → Admin access</li>
                    <li>• Front Office & Clerical → No stats tracking</li>
                    <li>• Staff/Senior Accountants → Full stats tracking</li>
                  </ul>
                </div>
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleImportEmployees}
                    disabled={importing}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="confirm-import-btn"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      "Import All"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="add-employee-btn">
                <Plus size={18} />
                Add Employee
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="add-employee-dialog">
              <DialogHeader>
              <DialogTitle>Add New Employee</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="John Doe"
                  required
                  data-testid="employee-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="john@smjcpa.com"
                  required
                  data-testid="employee-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Minimum 6 characters"
                  required
                  minLength={6}
                  data-testid="employee-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) =>
                    setFormData({ ...formData, role: value })
                  }
                >
                  <SelectTrigger data-testid="employee-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="position">Position</Label>
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) =>
                    setFormData({ ...formData, position: e.target.value })
                  }
                  placeholder="Staff Accountant"
                  data-testid="employee-position-input"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="tracks_stats"
                  checked={formData.tracks_stats}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, tracks_stats: checked })
                  }
                  data-testid="tracks-stats-checkbox"
                />
                <Label htmlFor="tracks_stats" className="text-sm text-muted-foreground">
                  Tracks billable statistics
                </Label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="birthday">Birthday (MM-DD)</Label>
                  <Input
                    id="birthday"
                    value={formData.birthday}
                    onChange={(e) =>
                      setFormData({ ...formData, birthday: e.target.value })
                    }
                    placeholder="05-19"
                    pattern="\d{2}-\d{2}"
                    data-testid="employee-birthday-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hire_date">Hire Date</Label>
                  <Input
                    id="hire_date"
                    type="date"
                    value={formData.hire_date}
                    onChange={(e) =>
                      setFormData({ ...formData, hire_date: e.target.value })
                    }
                    data-testid="employee-hire-date-input"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-employee-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Employee"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Users className="text-blue-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold text-foreground">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Users className="text-emerald-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Employees</p>
                <p className="text-2xl font-bold text-foreground">
                  {users.filter((u) => u.role === "employee").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Users className="text-amber-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Admins</p>
                <p className="text-2xl font-bold text-foreground">
                  {users.filter((u) => u.role === "admin").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle>All Users</CardTitle>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                size={18}
              />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full md:w-64"
                data-testid="search-users-input"
              />
            </div>
            <Button
              variant={showInactive ? "default" : "outline"}
              size="sm"
              onClick={() => setShowInactive((v) => !v)}
              data-testid="toggle-show-inactive-btn"
            >
              {showInactive ? "Hide inactive" : `Show inactive (${inactiveCount})`}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Stats</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow
                  key={user.id}
                  className={`table-row-hover ${user.is_active === false ? "opacity-55" : ""}`}
                  data-testid={`user-row-${user.id}`}
                >
                  <TableCell className="font-medium text-foreground">
                    {user.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{user.email}</TableCell>
                  <TableCell className="text-muted-foreground">{user.position || "-"}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${
                        user.role === "admin"
                          ? "bg-amber-500/30 text-amber-400 border border-amber-500/50"
                          : "bg-blue-500/30 text-blue-400 border border-blue-500/50"
                      }`}
                    >
                      {user.role}
                    </span>
                  </TableCell>
                  <TableCell>
                    {user.is_active === false ? (
                      <Badge variant="outline" className="text-rose-400 border-rose-500/50">
                        Inactive
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-emerald-500 border-emerald-500/40">
                        Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {user.tracks_stats !== false ? (
                        <Badge variant="outline" className="text-emerald-400 border-emerald-500/50 w-fit">
                          <BarChart3 size={12} className="mr-1" />
                          Stats
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground border-border w-fit">
                          No Stats
                        </Badge>
                      )}
                      {user.tracks_billings && (
                        <Badge variant="outline" className="text-amber-400 border-amber-500/50 w-fit">
                          $ Billings
                        </Badge>
                      )}
                      {user.is_supervisor && (
                        <Badge variant="outline" className="text-purple-400 border-purple-500/50 w-fit">
                          Supervisor
                        </Badge>
                      )}
                      {user.shelf_participant === false && (
                        <Badge variant="outline" className="text-muted-foreground border-border w-fit">
                          No Shelf
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm" title={user.last_login_at || ""} data-testid={`user-last-login-${user.id}`}>
                    {formatRelative(user.last_login_at)}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm" title={user.last_active_at || ""} data-testid={`user-last-active-${user.id}`}>
                    {formatRelative(user.last_active_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          user.is_active === false ? handleToggleActive(user) : setDeactivateTarget(user)
                        }
                        className={
                          user.is_active === false
                            ? "gap-1.5 text-emerald-600 border-emerald-500/40 hover:bg-emerald-500/10"
                            : "gap-1.5 text-muted-foreground hover:text-rose-500 hover:border-rose-500/40"
                        }
                        data-testid={`toggle-active-${user.id}`}
                      >
                        {user.is_active === false ? (
                          <>
                            <UserCheck size={14} /> Reactivate
                          </>
                        ) : (
                          <>
                            <UserX size={14} /> Deactivate
                          </>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleShelf(user)}
                        className={user.shelf_participant !== false ? "text-sky-400 hover:text-sky-400" : "text-muted-foreground hover:text-sky-400"}
                        title={user.shelf_participant !== false ? "Remove from the Digital Shelf" : "Add to the Digital Shelf"}
                        data-testid={`toggle-shelf-${user.id}`}
                      >
                        <LayoutList size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleStats(user.id, user.tracks_stats !== false, user.name)}
                        className={user.tracks_stats !== false ? "text-emerald-400 hover:text-emerald-400" : "text-muted-foreground hover:text-emerald-400"}
                        title={user.tracks_stats !== false ? "Disable stats tracking" : "Enable stats tracking"}
                        data-testid={`toggle-stats-${user.id}`}
                      >
                        <BarChart3 size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleBillings(user.id, user.tracks_billings, user.name)}
                        className={user.tracks_billings ? "text-amber-500 hover:text-amber-500" : "text-muted-foreground hover:text-amber-500"}
                        title={user.tracks_billings ? "Disable billings tracking" : "Enable billings tracking"}
                        data-testid={`toggle-billings-${user.id}`}
                      >
                        <DollarSign size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleSupervisor(user.id, user.is_supervisor, user.name)}
                        className={user.is_supervisor ? "text-purple-500 hover:text-purple-500" : "text-muted-foreground hover:text-purple-500"}
                        title={user.is_supervisor ? "Remove supervisor access" : "Make supervisor"}
                        data-testid={`toggle-supervisor-${user.id}`}
                      >
                        <Users size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditCelebrations(user)}
                        className={
                          user.birthday || user.hire_date
                            ? "text-pink-500 hover:text-pink-500"
                            : "text-muted-foreground hover:text-pink-500"
                        }
                        title="Edit birthday / hire date"
                        data-testid={`edit-celebrations-${user.id}`}
                      >
                        <Cake size={18} />
                      </Button>
                      {user.id !== currentUser?.id && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleResetPassword(user.id, user.name)}
                            disabled={resettingPassword === user.id}
                            className="text-muted-foreground hover:text-orange-600"
                            title={`Reset ${user.name}'s password`}
                            data-testid={`reset-password-${user.id}`}
                          >
                            {resettingPassword === user.id ? (
                              <Loader2 size={18} className="animate-spin" />
                            ) : (
                              <KeyRound size={18} />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleImpersonate(user.id, user.name)}
                            disabled={impersonating === user.id}
                            className="text-muted-foreground hover:text-blue-500"
                            title={`View as ${user.name}`}
                            data-testid={`impersonate-user-${user.id}`}
                          >
                            {impersonating === user.id ? (
                              <Loader2 size={18} className="animate-spin" />
                            ) : (
                              <Eye size={18} />
                            )}
                          </Button>
                        </>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-rose-600"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 size={18} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete User</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete {user.name}? This
                              action cannot be undone and will also remove all
                              their statistics.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(user.id, user.name)}
                              className="bg-rose-600 hover:bg-rose-700"
                              data-testid={`confirm-delete-${user.id}`}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredUsers.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No users found matching your search.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Birthday / Hire Date dialog */}
      <Dialog
        open={!!editingUser}
        onOpenChange={(open) => !open && setEditingUser(null)}
      >
        <DialogContent className="sm:max-w-md" data-testid="edit-celebrations-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? `Edit ${editingUser.name}'s Dates` : "Edit Dates"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-birthday">Birthday (MM-DD)</Label>
              <Input
                id="edit-birthday"
                value={editForm.birthday}
                onChange={(e) =>
                  setEditForm({ ...editForm, birthday: e.target.value })
                }
                placeholder="05-19"
                pattern="\d{2}-\d{2}"
                data-testid="edit-birthday-input"
              />
              <p className="text-xs text-muted-foreground">
                Leave blank to clear. Year is ignored.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-hire-date">Hire Date</Label>
              <Input
                id="edit-hire-date"
                type="date"
                value={editForm.hire_date}
                onChange={(e) =>
                  setEditForm({ ...editForm, hire_date: e.target.value })
                }
                data-testid="edit-hire-date-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditingUser(null)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCelebrations}
                disabled={savingEdit}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-celebrations-btn"
              >
                {savingEdit ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!deactivateTarget} onOpenChange={(o) => !o && setDeactivateTarget(null)}>
        <AlertDialogContent data-testid="deactivate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate {deactivateTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              They won't be able to log in, and they'll drop off the shelf, the leaderboard,
              birthdays and the daily email. Nothing is deleted — their stats and history stay,
              and you can reactivate them any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deactivate-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleToggleActive(deactivateTarget)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="deactivate-confirm-btn"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmployeeManagement;
